import { useMemo, useRef, useState } from 'react';
import { Image, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Link } from 'expo-router';
import type { HotelSummary } from '~/lib/api/types';
import type { PriceIntelligencePreview } from '~/lib/priceIntelligence.mock';
import { formatMoney } from '~/lib/money';
import { previewPriceIntelligence } from '~/lib/priceIntelligence.mock';
import { getHotelReviewData } from '~/lib/hotelReviews';
import { colors, fontFamily, price, radii, shadows, space, spacing, type } from '~/ui/tokens';
import { useCurrency } from '~/stores/useCurrency';
import { SkeletonBlock } from './ui/Skeleton';

type CityStandoutDealsProps = {
  city: string;
  hotels: HotelSummary[];
  onSelect: (hotel: HotelSummary) => void;
  getHref?: (hotel: HotelSummary) => string;
};

export function CityStandoutDealsSkeleton() {
  return (
    <View style={styles.section} accessibilityLabel="Loading standout hotel prices">
      <View style={styles.skeletonHeading}>
        <SkeletonBlock style={styles.skeletonTitle} />
        <SkeletonBlock style={styles.skeletonSubtitle} />
      </View>
      <View style={styles.skeletonTrack}>
        {[0, 1, 2].map((index) => (
          <View key={index} style={styles.skeletonCard}>
            <SkeletonBlock style={styles.skeletonImage} />
            <View style={styles.skeletonCopy}>
              <SkeletonBlock style={styles.skeletonName} />
              <SkeletonBlock style={styles.skeletonPrice} />
              <SkeletonBlock style={styles.skeletonMeta} />
            </View>
          </View>
        ))}
      </View>
    </View>
  );
}

export function CityStandoutDeals({ city, hotels, onSelect, getHref }: CityStandoutDealsProps) {
  useCurrency((state) => state.selectedCurrency);
  const scrollRef = useRef<ScrollView>(null);
  const scrollPosition = useRef(0);
  // The comparison provider is intentionally called for only the five banner
  // candidates, keeping a city search to five public-price checks. Only
  // properties with a usable photo qualify — this is a premium carousel, so a
  // placeholder tile would undercut it.
  const deals = useMemo(() => [...hotels]
    .filter((hotel) => Number(hotel.lowestPrice.amount) > 0 && Boolean(hotel.thumbnail))
    .sort((a, b) => Number(a.lowestPrice.amount) - Number(b.lowestPrice.amount))
    .slice(0, 5)
    .map((hotel) => ({ hotel, comparison: previewPriceIntelligence(hotel) }))
    // The carousel is a "cheaper than online" claim: only hotels with a
    // comparison belong in it, and without two of them it does not show.
    .filter((deal): deal is { hotel: HotelSummary; comparison: NonNullable<typeof deal.comparison> } => deal.comparison !== null), [hotels]);

  if (deals.length < 2) return null;

  function scrollBy(delta: number) {
    const next = Math.max(0, scrollPosition.current + delta);
    scrollPosition.current = next;
    scrollRef.current?.scrollTo({ x: next, animated: true });
  }

  return (
    <View style={styles.section}>
      <View style={styles.headingRow}>
        <View style={styles.headingCopy}>
          <View style={styles.titleRow}>
            <Ionicons name="pricetag" size={20} color={colors.pink} />
            <Text style={styles.title}>Great prices found in {city}</Text>
          </View>
          <Text style={styles.subtitle}>Handpicked stays at great prices.</Text>
        </View>
        <View style={styles.scrollControls}>
          <Pressable accessibilityRole="button" accessibilityLabel="Previous standout prices" onPress={() => scrollBy(-384)} style={({ pressed }) => [styles.scrollButton, pressed && styles.scrollButtonPressed]}>
            <Ionicons name="chevron-back" size={16} color={colors.inkSoft} />
          </Pressable>
          <Pressable accessibilityRole="button" accessibilityLabel="Next standout prices" onPress={() => scrollBy(384)} style={({ pressed }) => [styles.scrollButton, pressed && styles.scrollButtonPressed]}>
            <Ionicons name="chevron-forward" size={16} color={colors.inkSoft} />
          </Pressable>
        </View>
      </View>

      <ScrollView
        ref={scrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        onScroll={(event) => { scrollPosition.current = event.nativeEvent.contentOffset.x; }}
        scrollEventThrottle={16}
        contentContainerStyle={styles.track}
        accessibilityLabel={`Standout hotel prices in ${city}`}
      >
        {deals.map(({ hotel, comparison }) => comparison ? (
          <DealCard
            key={hotel.id}
            hotel={hotel}
            comparison={comparison}
            onSelect={() => onSelect(hotel)}
            href={getHref?.(hotel)}
          />
        ) : null)}
      </ScrollView>
    </View>
  );
}

function DealCard({
  hotel,
  comparison,
  onSelect,
  href,
}: {
  hotel: HotelSummary;
  comparison: PriceIntelligencePreview;
  onSelect: () => void;
  href?: string;
}) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const reviewData = useMemo(() => getHotelReviewData(hotel), [hotel]);
  // The comparable public price shown struck through, paired with the saving so
  // the two read as one comparison unit directly beneath our price.
  const onlinePrice = comparison.supplierQuotes[0]?.nightlyPrice;

  const cardContent = (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`View ${hotel.name} from ${formatMoney(hotel.lowestPrice)} per night${reviewData ? `, rated ${reviewData.scoreFormatted} ${reviewData.word}` : ''}, ${comparison.savingPercent}% cheaper than online`}
      onPress={onSelect}
      onPressIn={() => setPressed(true)}
      onPressOut={() => setPressed(false)}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      style={StyleSheet.flatten([styles.card, hovered && styles.cardHovered, pressed && styles.cardPressed])}
    >
      <View style={styles.imageWrap}>
        {hotel.thumbnail ? (
          <Image source={hotel.thumbnail} style={styles.image} accessibilityLabel={`${hotel.name} hotel`} />
        ) : (
          <View style={[styles.image, styles.imageFallback]}>
            <Ionicons name="image-outline" size={20} color={colors.inkFaint} />
          </View>
        )}
      </View>
      <View style={styles.cardBody}>
        <View style={styles.ratingSlot}>
          {reviewData ? (
            <View style={styles.reviewBadge} accessibilityLabel={`Rated ${reviewData.scoreFormatted} ${reviewData.word}`}>
              <Text style={styles.reviewScore}>{reviewData.scoreFormatted}</Text>
              <Text style={styles.reviewWord} numberOfLines={1}>{reviewData.word}</Text>
            </View>
          ) : null}
          <View style={styles.stars} accessibilityLabel={`${hotel.starRating ?? 0} star hotel`}>
            {Array.from({ length: Math.max(0, Math.min(5, Math.round(hotel.starRating ?? 0))) }).map((_, index) => (
              <Ionicons key={index} name="star" size={10} color="#F2A900" />
            ))}
          </View>
        </View>
        <View style={styles.nameSlot}>
          <Text style={styles.name} numberOfLines={2}>{hotel.name}</Text>
        </View>
        <View style={styles.comparisonSlot}>
          {onlinePrice ? (
          <View style={styles.comparisonRow}>
            <Text style={styles.onlineLabel}>Online</Text>
            <Text style={styles.onlinePrice}>{formatMoney(onlinePrice)}</Text>
            <View style={styles.savingPill}>
              <Text style={styles.savingText}>{comparison.savingPercent}% cheaper</Text>
            </View>
          </View>
          ) : null}
        </View>
        <View style={styles.priceSlot}>
          <View style={styles.priceRow}>
            <Text style={styles.price}>{formatMoney(hotel.lowestPrice)}</Text>
            <Text style={styles.night}>/night</Text>
          </View>
        </View>
        <View style={styles.taxesSlot}>
          <Text style={styles.taxes}>Online taxes &amp; fees included</Text>
        </View>
      </View>
    </Pressable>
  );

  return href ? (
    <Link href={href as any} asChild>
      {cardContent}
    </Link>
  ) : (
    cardContent
  );
}

const styles = StyleSheet.create({
  section: {
    borderRadius: radii.lg,
    backgroundColor: colors.pinkTint,
    paddingHorizontal: space.card,
    // A little more vertical breathing room top and bottom than before.
    paddingTop: space.card,
    paddingBottom: space.card,
    gap: space.card,
  },
  headingRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: space.internal },
  // Heading, subtitle and cards all share the container's left edge.
  headingCopy: { flex: 1, minWidth: 0, gap: space.related },
  titleRow: { flexDirection: 'row', alignItems: 'center', gap: space.related },
  title: { flexShrink: 1, fontFamily: fontFamily.bold, ...type.sectionTitle, color: colors.ink },
  subtitle: { fontFamily: fontFamily.regular, ...type.bodyRegular, color: colors.inkSoft },
  scrollControls: { flexDirection: 'row', gap: space.related, paddingTop: space.micro },
  scrollButton: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: colors.line, borderRadius: radii.full, backgroundColor: colors.card },
  scrollButtonPressed: { backgroundColor: colors.lineSoft },
  // paddingRight leaves the next card peeking past the last one to signal scroll.
  track: { gap: space.internal, paddingRight: space.cardLarge },
  card: {
    width: 368,
    height: 181,
    flexShrink: 0,
    flexDirection: 'row',
    backgroundColor: colors.card,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.md,
    padding: space.internal,
    gap: space.compact,
  },
  cardHovered: { borderColor: colors.brandLine },
  cardPressed: { opacity: 0.72 },
  // Fixed image footprint keeps every card the same height regardless of name.
  imageWrap: { width: 106, height: 147, position: 'relative', borderRadius: radii.sm, overflow: 'hidden' },
  image: { width: '100%', height: '100%', borderRadius: radii.sm, backgroundColor: colors.lineSoft },
  imageFallback: { alignItems: 'center', justifyContent: 'center' },
  cardBody: { flex: 1, minWidth: 0, height: 147, overflow: 'hidden' },
  ratingSlot: { height: 21, flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'flex-start', gap: space.micro },
  reviewBadge: { height: 19, maxWidth: '100%', flexDirection: 'row', alignItems: 'center', gap: space.micro, backgroundColor: colors.brand, borderRadius: 4, paddingHorizontal: 6, ...shadows.sm },
  reviewScore: { fontFamily: fontFamily.bold, fontSize: 11, color: colors.white, fontVariant: ['tabular-nums'] },
  reviewWord: { flexShrink: 1, fontFamily: fontFamily.semibold, fontSize: 10, color: colors.white },
  stars: { height: 19, flexDirection: 'row', alignItems: 'center', gap: 1 },
  nameSlot: { height: 38, justifyContent: 'flex-start' },
  // Two-line reservation so a short name leaves the price where a long name would.
  name: { height: type.bodyRegular.lineHeight * 2, flexShrink: 0, fontFamily: fontFamily.semibold, ...type.bodyRegular, color: colors.ink },
  priceSlot: { height: 31, justifyContent: 'center' },
  priceRow: { flexDirection: 'row', alignItems: 'baseline', gap: space.micro },
  price: { fontFamily: fontFamily.bold, ...price.compact, color: colors.brand },
  night: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkSoft },
  // Online price + saving read as a single comparison unit on one line.
  comparisonSlot: { height: 37, justifyContent: 'center', overflow: 'hidden' },
  comparisonRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', gap: space.micro },
  onlineLabel: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  onlinePrice: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint, textDecorationLine: 'line-through' },
  savingPill: { backgroundColor: colors.greenTint, borderRadius: radii.full, paddingVertical: spacing(0.625), paddingHorizontal: spacing(1.25) },
  savingText: { fontFamily: fontFamily.semibold, fontSize: 9, lineHeight: 12, color: colors.green },
  taxesSlot: { height: 20, justifyContent: 'flex-end' },
  taxes: { fontFamily: fontFamily.regular, fontSize: type.metadata.fontSize, color: colors.inkFaint },
  skeletonHeading: { gap: space.related },
  skeletonTitle: { width: 280, maxWidth: '70%', height: 22 },
  skeletonSubtitle: { width: 390, maxWidth: '88%', height: 14 },
  skeletonTrack: { flexDirection: 'row', gap: space.internal, overflow: 'hidden' },
  skeletonCard: { width: 368, height: 181, flexShrink: 0, flexDirection: 'row', gap: space.compact, padding: space.internal, borderWidth: 1, borderColor: colors.line, borderRadius: radii.md, backgroundColor: colors.card },
  skeletonImage: { width: 106, height: 147 },
  skeletonCopy: { flex: 1, gap: space.compact, paddingTop: space.micro },
  skeletonName: { width: '85%', height: 34 },
  skeletonPrice: { width: '55%', height: 28, marginTop: 'auto' },
  skeletonMeta: { width: '72%', height: 12 },
});
